import React, { createContext, useContext, useEffect, useState } from "react";

const ThemeContext = createContext();

const STORAGE_KEY = "arcade-color-mode";
// Rainbow order for the mode picker.
export const MODES = ["red", "purple", "green", "blue", "pink", "yellow"];

export const MODE_LABEL_KEYS = {
  red: "modeRed",
  purple: "modePurple",
  pink: "modePink",
  yellow: "modeYellow",
  blue: "modeBlue",
  green: "modeGreen",
};

// Swatch colors shown in the mode picker (match each theme’s look).
export const MODE_SWATCHES = {
  red: "#ff0000",
  purple: "#f4d35e",
  pink: "#a78bfa",
  yellow: "#ffffff",
  blue: "#3b82f6",
  green: "#22c55e",
};

const readStoredMode = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return "purple";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return MODES.includes(stored) ? stored : "purple";
  } catch {
    return "purple";
  }
};

const applyModeClass = (mode) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  MODES.forEach((name) => root.classList.remove(`theme-${name}`));
  root.classList.add(`theme-${mode}`);
};

const getNextMode = (current) => {
  const index = MODES.indexOf(current);
  return MODES[(index + 1) % MODES.length];
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const initial = readStoredMode();
    applyModeClass(initial);
    return initial;
  });

  useEffect(() => {
    applyModeClass(mode);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Ignore persistence failures
    }
  }, [mode]);

  const toggleMode = () => {
    setMode((current) => getNextMode(current));
  };

  const changeMode = (nextMode) => {
    if (MODES.includes(nextMode)) {
      setMode(nextMode);
    }
  };

  const nextMode = getNextMode(mode);

  return (
    <ThemeContext.Provider
      value={{
        mode,
        nextMode,
        nextModeLabelKey: MODE_LABEL_KEYS[nextMode],
        modeSwatches: MODE_SWATCHES,
        changeMode,
        toggleMode,
        modes: MODES,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
